import { useMemo, useCallback, type ReactElement } from 'react';
import {
  EXTENSION_LIST_EMPTY_ROW_SENTINEL,
  isValidExtensionCompositeId,
} from '@kirin/api-client/contracts/extension-validators';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { Plus, Trash2 } from 'lucide-react';

import { validateVersionConstraintValue } from '../allow-block/lib/cellValidation';

export interface ExtensionVersionRow {
  id: string;
  allVersions: boolean;
  versionConstraint: string;
}

const VERSION_MODE_ALL = 'all';
const VERSION_MODE_SPECIFIC = 'specific';

export { EXTENSION_LIST_EMPTY_ROW_SENTINEL };

function extensionIdValidationMessage(raw: string): string | undefined {
  const t = raw.trim();
  if (t === '' || t === EXTENSION_LIST_EMPTY_ROW_SENTINEL) {
    return undefined;
  }
  return isValidExtensionCompositeId(t.toLowerCase())
    ? undefined
    : 'Invalid extension id';
}

/**
 * Parses a single extension entry from API format into row shape.
 * Format: "publisher.name" (all versions) or "publisher.name@constraint" (e.g. "^1.0.0")
 */
function parseEntry(entry: string): ExtensionVersionRow {
  if (entry === EXTENSION_LIST_EMPTY_ROW_SENTINEL) {
    return { id: '', allVersions: true, versionConstraint: '' };
  }
  const atIndex = entry.indexOf('@');
  if (atIndex === -1) {
    return {
      id: entry,
      allVersions: true,
      versionConstraint: '',
    };
  }
  return {
    id: entry.slice(0, atIndex),
    allVersions: false,
    versionConstraint: entry.slice(atIndex + 1),
  };
}

/**
 * Serializes a row to API format.
 * All versions: "id" only. Specific version: "id@constraint" (or "id@" when constraint empty, to persist the mode).
 * Empty id row is serialized as sentinel so the new row persists in the form.
 */
function serializeRow(row: ExtensionVersionRow): string {
  const trimmedId = row.id.trim();
  if (!trimmedId) {
    return EXTENSION_LIST_EMPTY_ROW_SENTINEL;
  }
  if (row.allVersions) {
    return trimmedId;
  }
  const constraint = row.versionConstraint.trim();
  return constraint ? `${trimmedId}@${constraint}` : `${trimmedId}@`;
}

interface ExtensionVersionListFieldProps {
  value: string[];
  onChange: (value: string[]) => void;
  disabled?: boolean;
  settingKey: 'allowedExtensions' | 'blockedExtensions';
}

export function ExtensionVersionListField({
  value,
  onChange,
  disabled = false,
  settingKey,
}: ExtensionVersionListFieldProps): ReactElement {
  const rows = useMemo(
    () => (value.length > 0 ? value.map(parseEntry) : []),
    [value]
  );

  const updateRows = useCallback(
    (newRows: ExtensionVersionRow[]) => {
      const serialized = newRows
        .map(serializeRow)
        .filter(entry => entry.length > 0);
      onChange(serialized);
    },
    [onChange]
  );

  const updateRow = useCallback(
    (index: number, patch: Partial<ExtensionVersionRow>) => {
      const newRows = rows.map((r, i) =>
        i === index ? { ...r, ...patch } : r
      );
      updateRows(newRows);
    },
    [rows, updateRows]
  );

  const addRow = useCallback(() => {
    updateRows([...rows, { id: '', allVersions: true, versionConstraint: '' }]);
  }, [rows, updateRows]);

  const removeRow = useCallback(
    (index: number) => {
      updateRows(rows.filter((_, i) => i !== index));
    },
    [rows, updateRows]
  );

  const label =
    settingKey === 'allowedExtensions'
      ? 'Allowed Extensions'
      : 'Blocked Extensions';

  const nameAndVersionCell = 'min-w-0';
  const inputFullWidth = 'h-8 w-full min-w-0';

  return (
    <div className="space-y-3">
      <div className="rounded-md border overflow-x-auto">
        <div className="grid grid-cols-[1fr_1fr_auto_auto] gap-3 p-3 text-sm font-medium text-muted-foreground border-b bg-muted/30">
          <div>Extension name</div>
          <div>Version</div>
          <div>All versions / Version</div>
          <div className="w-9 shrink-0" />
        </div>
        {rows.length === 0 ? (
          <div className="p-4 text-sm text-muted-foreground">
            No entries. Add an extension with the button below.
          </div>
        ) : (
          <div className="divide-y">
            {rows.map((row, index) => {
              const extErr = extensionIdValidationMessage(row.id);
              const verErr = row.allVersions
                ? undefined
                : validateVersionConstraintValue(row.versionConstraint);
              return (
                <div
                  key={index}
                  className="grid grid-cols-[1fr_1fr_auto_auto] gap-3 p-3 items-start"
                >
                  <div className={nameAndVersionCell}>
                    <Input
                      value={row.id}
                      onChange={e => updateRow(index, { id: e.target.value })}
                      placeholder="e.g., publisher.name"
                      className={cn(
                        inputFullWidth,
                        extErr &&
                          'border-destructive focus-visible:ring-destructive'
                      )}
                      disabled={disabled}
                      aria-invalid={extErr ? true : undefined}
                    />
                    {extErr ? (
                      <p className="text-xs text-red-500 mt-1">{extErr}</p>
                    ) : null}
                  </div>
                  <div className={nameAndVersionCell}>
                    <Input
                      value={row.allVersions ? '*' : row.versionConstraint}
                      onChange={e =>
                        updateRow(index, {
                          versionConstraint: e.target.value,
                        })
                      }
                      placeholder="e.g., ^1.0.0 or >=2.0.0"
                      className={cn(
                        inputFullWidth,
                        verErr &&
                          'border-destructive focus-visible:ring-destructive'
                      )}
                      disabled={disabled || row.allVersions}
                      aria-invalid={verErr ? true : undefined}
                    />
                    {verErr ? (
                      <p className="text-xs text-red-500 mt-1">{verErr}</p>
                    ) : null}
                  </div>
                  <div className="flex items-center h-8">
                    <Select
                      value={
                        row.allVersions
                          ? VERSION_MODE_ALL
                          : VERSION_MODE_SPECIFIC
                      }
                      onValueChange={v => {
                        const allVersions = v === VERSION_MODE_ALL;
                        updateRow(index, {
                          allVersions,
                          versionConstraint: allVersions
                            ? ''
                            : row.versionConstraint,
                        });
                      }}
                      disabled={disabled}
                    >
                      <SelectTrigger size="sm" className="h-8 w-[140px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={VERSION_MODE_ALL}>
                          All versions
                        </SelectItem>
                        <SelectItem value={VERSION_MODE_SPECIFIC}>
                          Version
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center h-8">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive shrink-0"
                      onClick={() => removeRow(index)}
                      disabled={disabled}
                      aria-label={`Remove ${label} entry`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={addRow}
        disabled={disabled}
        className="gap-2"
      >
        <Plus className="h-4 w-4" />
        Add extension
      </Button>
      <p className="text-xs text-muted-foreground">
        Use format <code className="rounded bg-muted px-1">publisher.name</code>
        . For specific versions add a constraint (e.g.{' '}
        <code className="rounded bg-muted px-1">^1.0.0</code>,{' '}
        <code className="rounded bg-muted px-1">≥2.0.0</code>).
      </p>
    </div>
  );
}

ExtensionVersionListField.displayName = 'ExtensionVersionListField';
